'use client';

import { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, BarChart3, Sparkles, TrendingUp, Globe, 
  Smartphone, Monitor, Laptop, Navigation, Clock,
  MousePointerClick, ArrowUpRight, CalendarDays, ShieldAlert,
  Users, Zap, BarChart2, ChevronDown, Check, Link2, QrCode,
  ShoppingBag, Gift, ClipboardList, MapPin, Tablet, Play
} from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'

type TimeRange = '24h' | '7d' | '30d' | '90d'
type SourceType = null | 'link' | 'qr' | 'bio' | 'product' | 'referral' | 'form'

interface RecentClick {
  id: string
  title: string
  slug: string
  source: string
  country: string
  device: string
  time: string
  timestamp: string
}

interface MockData {
  totalClicks: number
  clicksToday: number
  clicksThisWeek: number
  clicksThisMonth: number
  uniqueVisitors: number
  botClicks: number
  recentClicks: RecentClick[]
  topLinks: { id: string; title: string; slug: string; clicks: number }[]
  topSources: { name: string; count: number }[]
  countries: { code: string; name: string; count: number }[]
  devices: { name: string; icon: any; count: number }[]
}

const BASE_MOCK_DATA: MockData = {
  totalClicks: 81200,
  clicksToday: 1240,
  clicksThisWeek: 8450,
  clicksThisMonth: 34200,
  uniqueVisitors: 56100,
  botClicks: 14,
  recentClicks: [
    { id: '1', title: 'Spring Promo Campaign', slug: 'spring-sale', source: 'link', country: 'US', device: 'mobile', time: '12:34 PM', timestamp: 'Today' },
    { id: '2', title: 'Main Bio Profile', slug: 'clark_kent', source: 'bio', country: 'IN', device: 'mobile', time: '12:30 PM', timestamp: 'Today' },
    { id: '3', title: 'Website QR Flyer', slug: 'flyer-qr', source: 'qr', country: 'GB', device: 'desktop', time: '12:20 PM', timestamp: 'Today' },
    { id: '4', title: 'Figma UI Kit Checkout', slug: 'figma-ui', source: 'product', country: 'DE', device: 'tablet', time: '11:58 AM', timestamp: 'Today' },
    { id: '5', title: 'Referral Invite Code', slug: 'ref-alex', source: 'referral', country: 'US', device: 'mobile', time: '11:45 AM', timestamp: 'Today' },
    { id: '6', title: 'Feedback Form Survey', slug: 'survey-feedback', source: 'form', country: 'FR', device: 'desktop', time: '11:15 AM', timestamp: 'Today' }
  ],
  topLinks: [
    { id: 'l1', title: 'Spring Promo Campaign', slug: 'spring-sale', clicks: 2450 },
    { id: 'l2', title: 'Bio Portfolio Link', slug: 'portfolio', clicks: 1890 },
    { id: 'l3', title: 'SaaS Boilerplate PDF', slug: 'boilerplate', clicks: 1240 }
  ],
  topSources: [
    { name: 'Twitter / X', count: 24500 },
    { name: 'Instagram Bio', count: 18900 },
    { name: 'Direct Search', count: 14200 },
    { name: 'YouTube Video', count: 9800 }
  ],
  countries: [
    { code: 'US', name: 'United States', count: 32400 },
    { code: 'GB', name: 'United Kingdom', count: 14500 },
    { code: 'IN', name: 'India', count: 12100 },
    { code: 'DE', name: 'Germany', count: 9800 }
  ],
  devices: [
    { name: 'mobile', icon: Smartphone, count: 45200 },
    { name: 'desktop', icon: Monitor, count: 31200 },
    { name: 'tablet', icon: Tablet, count: 4800 }
  ]
}

const getDeviceIcon = (device: string) => {
  switch (device) {
    case 'mobile': return Smartphone
    case 'tablet': return Tablet
    default: return Monitor
  }
}

const getSourceIcon = (source: string) => {
  switch (source) {
    case 'link': return Link2
    case 'qr': return QrCode
    case 'bio': return Globe
    case 'product': return ShoppingBag
    case 'referral': return Gift
    case 'form': return ClipboardList
    default: return Link2
  }
}

const DATA_24H: ChartDataPoint[] = [
  { label: '09:00', clicks: 120 },
  { label: '12:00', clicks: 340 },
  { label: '15:00', clicks: 250 },
  { label: '18:00', clicks: 480 },
  { label: '21:00', clicks: 610 },
  { label: '00:00', clicks: 210 },
  { label: '03:00', clicks: 90 },
  { label: '06:00', clicks: 180 }
]

const DATA_7D: ChartDataPoint[] = [
  { label: 'Mon', clicks: 1200 },
  { label: 'Tue', clicks: 2100 },
  { label: 'Wed', clicks: 1800 },
  { label: 'Thu', clicks: 2900 },
  { label: 'Fri', clicks: 3400 },
  { label: 'Sat', clicks: 1500 },
  { label: 'Sun', clicks: 2200 }
]

const DATA_30D: ChartDataPoint[] = [
  { label: 'Week 1', clicks: 9800 },
  { label: 'Week 2', clicks: 14500 },
  { label: 'Week 3', clicks: 12100 },
  { label: 'Week 4', clicks: 19400 }
]

export default function AnalyticsDashboardClient() {
  const [range, setRange] = useState<TimeRange>('7d')
  const [source, setSource] = useState<SourceType>(null)
  const [isSourceDropdownOpen, setIsSourceDropdownOpen] = useState(false)

  // Dynamic multipliers based on range and source
  let rangeMult = 1
  if (range === '24h') rangeMult = 0.02
  else if (range === '7d') rangeMult = 0.15
  else if (range === '30d') rangeMult = 0.5
  else if (range === '90d') rangeMult = 1.0

  let srcMult = 1
  if (source === 'link') srcMult = 0.55
  else if (source === 'qr') srcMult = 0.15
  else if (source === 'bio') srcMult = 0.12
  else if (source === 'product') srcMult = 0.08
  else if (source === 'referral') srcMult = 0.06
  else if (source === 'form') srcMult = 0.04

  const totalEngagement = Math.round(BASE_MOCK_DATA.totalClicks * rangeMult * srcMult)
  const clicksToday = Math.round(BASE_MOCK_DATA.clicksToday * (range === '24h' ? 1 : rangeMult) * srcMult)
  const clicksThisWeek = Math.round(BASE_MOCK_DATA.clicksThisWeek * (range === '24h' || range === '7d' ? 1 : rangeMult) * srcMult)
  const clicksThisMonth = Math.round(BASE_MOCK_DATA.clicksThisMonth * (range !== '90d' ? 1 : rangeMult) * srcMult)
  const uniqueVisitors = Math.round(BASE_MOCK_DATA.uniqueVisitors * rangeMult * srcMult)
  const flaggedTraffic = Math.round(BASE_MOCK_DATA.botClicks * rangeMult * srcMult)

  // Chart labels & values
  const chartLabels = 
    range === '24h' ? ['09:00', '12:00', '15:00', '18:00', '21:00', '00:00', '03:00', '06:00'] :
    range === '7d' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] :
    range === '30d' ? ['Wk 1', 'Wk 2', 'Wk 3', 'Wk 4'] :
    ['Month 1', 'Month 2', 'Month 3']

  const baseClicks = 
    range === '24h' ? [120, 340, 250, 480, 610, 210, 90, 180] :
    range === '7d' ? [1200, 2100, 1800, 2900, 3400, 1500, 2200] :
    range === '30d' ? [9800, 14500, 12100, 19400] :
    [24000, 31000, 26200]

  const activeData = chartLabels.map((label, idx) => ({
    label,
    clicks: Math.round((baseClicks[idx] || 100) * srcMult)
  }))

  const maxVal = Math.max(...activeData.map(d => d.clicks)) || 1
  const chartWidth = 500
  const chartHeight = 160
  const points = activeData.map((d, i) => {
    const x = (i / (activeData.length - 1)) * chartWidth
    const y = chartHeight - (d.clicks / maxVal) * (chartHeight - 20) - 10
    return `${x},${y}`
  }).join(' ')

  const fillPoints = `0,${chartHeight} ${points} ${chartWidth},${chartHeight}`

  const filteredClicks = BASE_MOCK_DATA.recentClicks.filter(click => {
    if (source === null) return true
    return click.source === source
  })

  const scaledTopLinks = BASE_MOCK_DATA.topLinks.map(link => ({
    ...link,
    clicks: Math.round(link.clicks * rangeMult * srcMult)
  }))

  const scaledSources = BASE_MOCK_DATA.topSources.map(src => ({
    ...src,
    count: Math.round(src.count * rangeMult * srcMult)
  }))

  const scaledCountries = BASE_MOCK_DATA.countries.map(c => ({
    ...c,
    count: Math.round(c.count * rangeMult * srcMult)
  }))

  const scaledDevices = BASE_MOCK_DATA.devices.map(d => ({
    ...d,
    count: Math.round(d.count * rangeMult * srcMult)
  }))

  const sourceTabs = [
    { label: 'All', value: null, icon: BarChart2, count: totalEngagement },
    { label: 'Links', value: 'link' as SourceType, icon: Link2, count: Math.round(totalEngagement * 0.55) },
    { label: 'QR Code', value: 'qr' as SourceType, icon: QrCode, count: Math.round(totalEngagement * 0.15) },
    { label: 'Bio Page', value: 'bio' as SourceType, icon: Globe, count: Math.round(totalEngagement * 0.12) },
    { label: 'Product', value: 'product' as SourceType, icon: ShoppingBag, count: Math.round(totalEngagement * 0.08) },
    { label: 'Referral', value: 'referral' as SourceType, icon: Gift, count: Math.round(totalEngagement * 0.06) },
    { label: 'Form & Poll', value: 'form' as SourceType, icon: ClipboardList, count: Math.round(totalEngagement * 0.04) }
  ]

  const activeTab = sourceTabs.find(t => t.value === source) || sourceTabs[0]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  }

  // Heatmap values
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const hoursList = Array.from({ length: 24 })

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white">
      <GeometricBackground />
      <MarketingNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 max-w-[1400px] mx-auto z-10 flex flex-col items-center">
        <motion.div 
          className="text-center space-y-6 max-w-3xl"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center gap-2 justify-center">
            <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
              Real-time Tracker
            </span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.1]">
            Link Analytics Tracker<br />
            <span className="text-gray-500">Visitor dashboards and</span><br />
            Click tracking software
          </motion.h1>

          <motion.p variants={itemVariants} className="text-base sm:text-lg text-white/50 font-light max-w-xl mx-auto leading-relaxed">
            Acquire deep, actionable data on every click. Monitor device types, geolocations, and traffic origins in real-time with visual summaries.
          </motion.p>
        </motion.div>

        {/* Dashboard Mockup Panel */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="w-full max-w-5xl mt-12 bg-background/40 border border-white/10 rounded-3xl p-6 md:p-8 backdrop-blur-xl shadow-[0_0_80px_rgba(0,102,255,0.05)] space-y-6"
        >
          {/* Dashboard Header controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
            <div>
              <h2 className="text-sm font-mono tracking-widest text-primary/80 uppercase flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Live Analytics Sandbox
              </h2>
              <p className="text-[10px] text-white/40 font-mono">ID: lk_dash_simulation • Updated just now</p>
            </div>
            
            <div className="flex gap-1 bg-white/5 p-1 rounded-xl border border-white/5">
              {(['24h', '7d', '30d', '90d'] as TimeRange[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setRange(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all ${
                    range === t 
                      ? 'bg-primary text-white' 
                      : 'text-white/40 hover:text-white'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Source Filter Switcher */}
          <div className="relative w-full">
            {/* Mobile Trigger */}
            <button
              onClick={() => setIsSourceDropdownOpen(!isSourceDropdownOpen)}
              className="flex sm:hidden items-center justify-between w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-sm font-medium text-white transition-all hover:bg-white/[0.05]"
            >
              <div className="flex items-center gap-2.5">
                <activeTab.icon className="w-4 h-4 text-primary" />
                <span>{activeTab.label}</span>
                <span className="px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-primary/20 text-primary">
                  {activeTab.count}
                </span>
              </div>
              <ChevronDown className={`w-4 h-4 text-white/40 transition-transform duration-200 ${isSourceDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Mobile Dropdown Menu */}
            {isSourceDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 p-1.5 bg-[#0A0A0A] border border-white/[0.08] rounded-xl shadow-2xl z-50 sm:hidden">
                {sourceTabs.map((tab) => (
                  <button
                    key={tab.label}
                    onClick={() => {
                      setSource(tab.value)
                      setIsSourceDropdownOpen(false)
                    }}
                    className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm transition-all ${source === tab.value
                      ? 'bg-white/[0.08] text-white'
                      : 'text-white/40 hover:text-white/60 hover:bg-white/[0.02]'
                      }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <tab.icon className={`w-4 h-4 ${source === tab.value ? 'text-primary' : ''}`} />
                      <span>{tab.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono ${source === tab.value ? 'bg-primary/20 text-primary' : 'bg-white/[0.04] text-white/30'}`}>
                        {tab.count}
                      </span>
                      {source === tab.value && <Check className="w-3.5 h-3.5 text-primary" />}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Desktop View */}
            <div className="hidden sm:flex flex-wrap items-center gap-1 bg-white/[0.03] rounded-xl p-1 w-fit border border-white/[0.02]">
              {sourceTabs.map((tab) => (
                <button
                  key={tab.label}
                  onClick={() => setSource(tab.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${source === tab.value
                    ? 'bg-white/[0.08] text-white shadow-sm'
                    : 'text-white/40 hover:text-white/60 hover:bg-white/[0.01]'
                    }`}
                >
                  <tab.icon className={`w-3 h-3 ${source === tab.value ? 'text-primary' : ''}`} />
                  <span className="whitespace-nowrap">{tab.label}</span>
                  <span className={`ml-1 px-1.5 py-0.5 rounded-md text-[10px] font-mono ${source === tab.value ? 'bg-primary/20 text-primary' : 'bg-white/[0.04] text-white/30'}`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Metric cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {[
              { label: source === 'qr' ? 'Total Scans' : source === 'bio' ? 'Bio Clicks' : source === 'link' ? 'Total Clicks' : source === 'product' ? 'Store Views' : source === 'referral' ? 'Referral Clicks' : source === 'form' ? 'Submissions' : 'Total Engagement', value: totalEngagement, icon: MousePointerClick, color: 'text-primary' },
              { label: 'Today', value: clicksToday, icon: TrendingUp, color: 'text-primary' },
              { label: 'This Week', value: clicksThisWeek, icon: ArrowUpRight, color: 'text-primary' },
              { label: 'This Month', value: clicksThisMonth, icon: CalendarDays, color: 'text-primary' },
              { label: 'Unique Visitors', value: uniqueVisitors, icon: Users, color: 'text-primary' },
              { label: 'Flagged Clicks', value: flaggedTraffic, icon: ShieldAlert, color: 'text-red-500' },
            ].map((stat) => {
              const Icon = stat.icon
              return (
                <div key={stat.label} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 hover:bg-white/[0.04] transition-all duration-300">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[9px] font-mono text-white/40 uppercase tracking-wider">{stat.label}</p>
                    <Icon className={`w-3.5 h-3.5 ${stat.color}`} />
                  </div>
                  <p className="text-xl font-bold text-white font-mono">{stat.value.toLocaleString()}</p>
                </div>
              )
            })}
          </div>

          {/* Interactive Chart */}
          <div className="p-6 bg-white/[0.01] border border-white/5 rounded-3xl space-y-4">
            <div className="flex items-center justify-between text-xs text-white/40">
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Traffic Distribution</span>
              <span className="font-mono text-[10px] uppercase">Clicks over time ({range})</span>
            </div>

            {/* Simulated Chart Container */}
            <div className="w-full h-44 flex items-end relative pt-4">
              <svg className="w-full h-full overflow-visible" viewBox="0 0 500 160" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="chartGlow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0066FF" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#0066FF" stopOpacity="0.0" />
                  </linearGradient>
                </defs>

                {/* Grid Lines */}
                <line x1="0" y1="40" x2="500" y2="40" stroke="#ffffff08" strokeWidth="1" strokeDasharray="4 4" />
                <line x1="0" y1="80" x2="500" y2="80" stroke="#ffffff08" strokeWidth="1" strokeDasharray="4 4" />
                <line x1="0" y1="120" x2="500" y2="120" stroke="#ffffff08" strokeWidth="1" strokeDasharray="4 4" />

                {/* Shaded Area */}
                <polygon points={fillPoints} fill="url(#chartGlow)" />
                {/* Line Path */}
                <polyline fill="none" stroke="#0066FF" strokeWidth="2.5" points={points} />
              </svg>
            </div>

            {/* Labels Row */}
            <div className="flex justify-between text-[10px] font-mono text-white/30 border-t border-white/5 pt-3">
              {activeData.map((d) => (
                <span key={d.label}>{d.label}</span>
              ))}
            </div>
          </div>

          {/* Dashboard Columns (Split Layout) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start pt-2">
            {/* Left Column — Click Log + Time Heatmap */}
            <div className="lg:col-span-2 space-y-6">
              {/* Recent Clicks Log */}
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                <div className="p-4 sm:p-5 border-b border-white/[0.06]">
                  <h2 className="text-sm font-semibold text-white">
                    {source === 'qr' ? 'Scan Log' : source === 'bio' ? 'Bio Page Clicks' : 'Engagement Click Log'}
                  </h2>
                </div>

                <div className="divide-y divide-white/[0.04]">
                  {filteredClicks.length === 0 ? (
                    <div className="p-5 flex flex-col items-center justify-center py-10 text-center">
                      <p className="text-xs text-white/20">No matching clicks logged for source selection.</p>
                    </div>
                  ) : (
                    filteredClicks.map((click) => {
                      const DeviceIcon = getDeviceIcon(click.device)
                      const SourceIcon = getSourceIcon(click.source)
                      return (
                        <div key={click.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/[0.02] transition-colors">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <SourceIcon className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-white truncate">{click.title}</p>
                            <p className="text-[10px] text-white/20 font-mono">/r/{click.slug}</p>
                          </div>
                          <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0 text-white/40 text-[11px] font-mono">
                            <MapPin className="w-3.5 h-3.5" />
                            <span>{click.country}</span>
                          </div>
                          <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
                            <DeviceIcon className="w-3.5 h-3.5 text-white/25" />
                          </div>
                          <div className="text-right flex-shrink-0 min-w-[70px]">
                            <p className="text-[10px] text-white/50 font-mono">{click.time}</p>
                            <p className="text-[9px] text-white/20 font-mono">{click.timestamp}</p>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>

              {/* Time Heatmap section */}
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                <div className="p-5 border-b border-white/[0.06]">
                  <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Clock className="w-4 h-4 text-white/40" />
                    Engagement Hour Heatmap
                  </h2>
                  <p className="text-xs text-white/30 mt-1">See when your audience clicks or scans the most</p>
                </div>
                <div className="p-5 overflow-x-auto">
                  <div className="flex flex-col gap-1.5 min-w-[500px]">
                    {weekDays.map((day, dIdx) => (
                      <div key={day} className="flex items-center gap-2">
                        <span className="w-8 text-[10px] font-mono text-white/30">{day}</span>
                        <div className="flex-1 flex gap-1">
                          {hoursList.map((_, hIdx) => {
                            // Pseudo random activity based on index
                            const raw = Math.sin((dIdx + 1) * (hIdx + 1)) * 0.5 + 0.5
                            const opacityVal = raw * srcMult
                            let bgClass = 'bg-white/[0.03]'
                            if (opacityVal > 0.6) bgClass = 'bg-primary'
                            else if (opacityVal > 0.3) bgClass = 'bg-primary/60'
                            else if (opacityVal > 0.1) bgClass = 'bg-primary/20'

                            return (
                              <div 
                                key={hIdx} 
                                className={`h-3 flex-1 rounded-sm ${bgClass}`}
                                title={`${day} hour ${hIdx}: ${Math.round(raw * 45)} click events`}
                              />
                            )
                          })}
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between pl-10 text-[8px] font-mono text-white/20 pt-1">
                      <span>12 AM</span>
                      <span>6 AM</span>
                      <span>12 PM</span>
                      <span>6 PM</span>
                      <span>11 PM</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column — Top links, sources, locations, devices */}
            <div className="space-y-6">
              {/* Top Links */}
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                <div className="p-5 border-b border-white/[0.06]">
                  <h2 className="text-sm font-semibold text-white">
                    {source === 'qr' ? 'Top QR Codes' : 'Top Click Targets'}
                  </h2>
                </div>
                <div className="p-4 space-y-1">
                  {scaledTopLinks.map((link, idx) => (
                    <div key={link.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/[0.03] transition-colors">
                      <span className="w-5 h-5 flex items-center justify-center text-[10px] font-bold text-white/20">{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-white truncate">{link.title}</p>
                        <p className="text-[10px] text-white/20 font-mono">/r/{link.slug}</p>
                      </div>
                      <span className="text-xs font-bold text-white tabular-nums">{link.clicks.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Referrers */}
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                <div className="p-5 border-b border-white/[0.06]">
                  <h2 className="text-sm font-semibold text-white">Traffic Sources</h2>
                </div>
                <div className="p-5 space-y-3">
                  {(() => {
                    const total = scaledSources.reduce((s, item) => s + item.count, 0) || 1
                    const maxCount = scaledSources[0]?.count || 1
                    return scaledSources.map((item) => {
                      const pct = Math.round((item.count / total) * 100)
                      return (
                        <div key={item.name} className="space-y-1">
                          <div className="flex justify-between items-center text-xs font-mono">
                            <span className="text-white/60">{item.name}</span>
                            <div className="flex gap-2 text-white/45">
                              <span>{item.count.toLocaleString()}</span>
                              <span>{pct}%</span>
                            </div>
                          </div>
                          <div className="h-1 bg-white/[0.04] rounded-full overflow-hidden">
                            <div className="h-full bg-primary/60" style={{ width: `${(item.count / maxCount) * 100}%` }} />
                          </div>
                        </div>
                      )
                    })
                  })()}
                </div>
              </div>

              {/* Top Locations */}
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                <div className="p-5 border-b border-white/[0.06]">
                  <h2 className="text-sm font-semibold text-white">Top Locations</h2>
                </div>
                <div className="p-5 space-y-3">
                  {(() => {
                    const total = scaledCountries.reduce((s, item) => s + item.count, 0) || 1
                    const maxCount = scaledCountries[0]?.count || 1
                    return scaledCountries.map((c) => {
                      const pct = Math.round((c.count / total) * 100)
                      return (
                        <div key={c.code} className="space-y-1">
                          <div className="flex justify-between items-center text-xs font-mono">
                            <span className="text-white/60 flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-primary" /> {c.name}
                            </span>
                            <div className="flex gap-2 text-white/45">
                              <span>{c.count.toLocaleString()}</span>
                              <span>{pct}%</span>
                            </div>
                          </div>
                          <div className="h-1 bg-white/[0.04] rounded-full overflow-hidden">
                            <div className="h-full bg-primary/60" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                          </div>
                        </div>
                      )
                    })
                  })()}
                </div>
              </div>

              {/* Device breakdown */}
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
                <div className="p-5 border-b border-white/[0.06]">
                  <h2 className="text-sm font-semibold text-white">Device Breakdown</h2>
                </div>
                <div className="p-4 space-y-1">
                  {(() => {
                    const total = scaledDevices.reduce((s, item) => s + item.count, 0) || 1
                    return scaledDevices.map((item) => {
                      const DeviceIcon = item.icon
                      const pct = Math.round((item.count / total) * 100)
                      return (
                        <div key={item.name} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/[0.02] transition-colors font-mono">
                          <DeviceIcon className="w-4 h-4 text-white/30" />
                          <span className="text-xs text-white/60 capitalize flex-1">{item.name}</span>
                          <span className="text-xs text-white/30">{pct}%</span>
                          <span className="text-xs text-white font-bold w-12 text-right">{item.count.toLocaleString()}</span>
                        </div>
                      )
                    })
                  })()}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-20 border-t border-white/5 relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Real-Time Event Streams</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Don&apos;t wait for hourly updates. Monitor link scans and checkout completions as they occur with a live dashboard activity log.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Strict Privacy Compliance</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Fully GDPR & CCPA compliant. We collect aggregate location data and device models without storing identifying cookies or tracking IPs.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">UTM Parameter Tracking</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Attach parameters to short links dynamically. Auto-organize campaigns by source, medium, and content keywords inside the analytics view.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5 text-center relative z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.05),transparent_70%)] blur-[100px] pointer-events-none" />
        <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">Measure what matters</h2>
        <p className="text-white/40 text-sm max-w-md mx-auto mt-4 font-light">Acquire instant click intelligence. Make informed branding decisions with enterprise analytics.</p>
        <Link 
          href="/auth/sign-up" 
          className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 bg-primary text-white font-bold rounded-full hover:scale-105 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] active:scale-95"
        >
          Access Free Dashboard <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <MarketingFooter />
    </div>
  )
}
